import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowUpRight, Check, HelpCircle, Loader2, MessageSquareReply, Paperclip, Target, X } from 'lucide-react';
import ApprovalReview from 'features/approvals/ApprovalReview';
import { publicationError } from 'features/approvals/publicationError';
import { agentKeys, useAnswerAsk, useInbox } from 'features/agents/hooks';
import { deleteInitiative, startInitiative } from 'features/initiatives/api';
import { useInitiative } from 'features/initiatives/hooks';
import { initiativePath } from 'features/initiatives/initiativePaths';
import { draftReviewPath } from 'features/sandbox/utils/previewDestination';
import { useBranchPublication } from 'features/sandbox/hooks/useBranchPublication';
import { useSandboxArchive, useSandboxChanges } from 'features/views/hooks/useOntologyViews';
import Markdown from 'shared/ui/Markdown';

function InitiativeLink({ card }) {
  if (!card.initiative_id) return null;
  return (
    <Link
      to={initiativePath(card.initiative_id, card.owner_agent_id)}
      className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900"
    >
      <Target size={12} />
      <span className="truncate">{card.initiative_title || 'Open the work'}</span>
      <ArrowUpRight size={12} />
    </Link>
  );
}

/** What a chat message answered for the agent's work (ADR-0010), shown under the reply. */
function ReplyPassedOnCard({ card }) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700" data-testid="reply-passed-on-card">
      <Check size={13} className="shrink-0 text-emerald-600" />
      <span>
        {card.outcome === 'answered' ? 'Your message answered ' : 'Passed on about '}
        <span className="font-medium text-slate-900"><Markdown inline>{card.subject || 'a question'}</Markdown></span>
      </span>
      <InitiativeLink card={card} />
    </div>
  );
}

/** A question an initiative is waiting on. Answering wakes the owning agent, which the card acknowledges. */
function AskCard({ card, bare = false, onAnswered }) {
  const inbox = useInbox();
  const answer = useAnswerAsk();
  const [reply, setReply] = useState('');
  const [files, setFiles] = useState([]);
  const [answeredWith, setAnsweredWith] = useState(null);
  const [outcome, setOutcome] = useState(null);
  // Words saying the answer is still to come: the agent has them and the question stays open.
  const [noted, setNoted] = useState(null);
  const choices = Array.isArray(card.choices) ? card.choices.filter(Boolean) : [];
  // Open: the person's own questions, and ones they passed on but can still answer.
  const openAskIds = new Set([
    ...(inbox.data?.items || []).filter((item) => item.kind === 'ask'),
    ...(inbox.data?.waiting || []).filter((item) => item.forwarded),
  ].map((item) => String(item.id)));
  const closed = answeredWith !== null || (inbox.isSuccess && !openAskIds.has(String(card.ask_id)));
  const alsoAnswered = (outcome?.also_answered || []).map((item) => item.title).filter(Boolean);

  // Options are shortcuts: the person can always answer in their own words or with a file.
  const submit = (payload) => {
    const attached = files.map((file) => file.name).join(', ');
    answer.mutate(
      { askId: card.ask_id, ...payload, files },
      {
        onSuccess: (data) => {
          const words = payload.choice || payload.message || attached;
          if (data?.kept_open) {
            setReply('');
            setFiles([]);
            setNoted(words);
            return;
          }
          setOutcome(data || null);
          setAnsweredWith(words);
          onAnswered?.({ answer: words, outcome: data || null });
        },
      }
    );
  };

  return (
    <div className={bare ? '' : 'animate-slideUp rounded-xl border border-amber-200 bg-amber-50/60 p-4 shadow-sm'} data-testid="agent-ask-card">
      {bare ? null : (
        <div className="flex items-start gap-2">
          <HelpCircle size={16} className="mt-0.5 shrink-0 text-amber-600" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900"><Markdown inline>{card.subject || 'Question for you'}</Markdown></p>
            <InitiativeLink card={card} />
          </div>
        </div>
      )}
      {closed ? (
        <div className="mt-2 space-y-0.5 text-xs">
          <p className="flex items-center gap-1.5 text-emerald-700">
            <Check size={13} />
            {outcome?.passed_on
              ? 'Sent. This question had already closed, so your reply went to the work as a message.'
              : answeredWith !== null
                ? `Got it — passed to ${card.agent_name || 'the agent'}.`
                : 'Answered'}
          </p>
          {answeredWith && !outcome?.passed_on ? <p className="line-clamp-2 text-slate-600">You said: {answeredWith}</p> : null}
          {alsoAnswered.length ? (
            <p className="text-slate-600">Your reply also answered: {alsoAnswered.join('; ')}</p>
          ) : null}
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          {noted ? (
            <p className="text-xs text-slate-600" data-testid="ask-kept-open">
              Noted: {card.agent_name || 'the agent'} knows the answer is still to come. The question stays here until you answer it.
            </p>
          ) : null}
          {choices.length ? (
            <div className="flex flex-wrap gap-1.5">
              {choices.map((choice) => (
                <button
                  key={choice}
                  type="button"
                  disabled={answer.isPending}
                  onClick={() => submit({ choice, message: reply.trim() })}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 hover:bg-slate-50 disabled:opacity-50"
                >
                  {choice}
                </button>
              ))}
            </div>
          ) : null}
          <form
            className="flex items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (reply.trim() || files.length) submit({ message: reply.trim() });
            }}
          >
            <div className="min-w-0 flex-1 space-y-1">
              <textarea
                value={reply}
                onChange={(event) => setReply(event.target.value)}
                rows={2}
                placeholder={choices.length ? 'Or answer in your own words' : 'Your answer'}
                className="min-h-[2.5rem] w-full resize-none rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-800 focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-50"
              />
              {files.length ? (
                <ul className="flex flex-wrap gap-1.5" aria-label="Attached files">
                  {files.map((file, index) => (
                    <li key={`${file.name}-${index}`} className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-700">
                      <Paperclip size={11} />
                      <span className="max-w-[12rem] truncate">{file.name}</span>
                      <button
                        type="button"
                        aria-label={`Remove ${file.name}`}
                        onClick={() => setFiles((current) => current.filter((_, position) => position !== index))}
                        className="text-slate-400 hover:text-slate-700"
                      >
                        <X size={11} />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            <label className="inline-flex h-9 cursor-pointer items-center rounded-lg border border-slate-300 bg-white px-2.5 text-slate-600 hover:bg-slate-50" title="Attach a file">
              <Paperclip size={14} />
              <span className="sr-only">Attach a file</span>
              <input
                type="file"
                multiple
                className="sr-only"
                onChange={(event) => {
                  const chosen = Array.from(event.target.files || []);
                  setFiles((current) => [...current, ...chosen].slice(0, 10));
                  event.target.value = '';
                }}
              />
            </label>
            <button
              type="submit"
              disabled={answer.isPending || (!reply.trim() && !files.length)}
              className="inline-flex h-9 items-center gap-1 rounded-lg bg-sky-500 px-3 text-xs font-semibold text-white disabled:opacity-40"
            >
              {answer.isPending ? <Loader2 size={13} className="animate-spin" /> : <MessageSquareReply size={13} />}
              Answer
            </button>
          </form>
          {answer.error ? <p role="alert" className="text-xs text-rose-700">{answer.error.message}</p> : null}
        </div>
      )}
    </div>
  );
}

/**
 * Work the agent suggests. Nothing runs until the person starts it. A draft
 * nobody suggested (`suggested: false`) offers only Start: "Not now" deletes.
 * A failed Start stays on the card with the reason.
 */
function ProposalCard({ card, bare = false }) {
  const queryClient = useQueryClient();
  const work = useInitiative(card.initiative_id);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const suggested = card.suggested !== false;
  const status = work.data?.status || (work.isError ? 'dismissed' : 'draft');
  const decided = status !== 'draft';
  // A Superadmin can see Otto's suggestions; only a customer owner starts or dismisses work.
  const readOnly = work.data?.can_control === false;
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['initiatives'] });
    queryClient.invalidateQueries({ queryKey: ['home'] });
    queryClient.invalidateQueries({ queryKey: agentKeys.feed() });
    queryClient.invalidateQueries({ queryKey: agentKeys.inbox() });
  };
  const act = async (kind) => {
    setBusy(kind);
    setError('');
    try {
      if (kind === 'start') await startInitiative(card.initiative_id);
      else await deleteInitiative(card.initiative_id);
      refresh();
    } catch (err) {
      setError(err?.message || 'That did not work. Try again.');
    } finally {
      setBusy(null);
    }
  };
  const steps = Array.isArray(card.steps) ? card.steps : [];
  return (
    <div className={bare ? '' : 'animate-slideUp rounded-xl border border-cyan-200 bg-gradient-to-br from-white to-cyan-50/50 p-4 shadow-sm'} data-testid="work-proposal-card">
      <p className="text-xs text-slate-500">
        {!suggested ? 'Nothing runs until you start it' : card.suggested_by ? `Suggested by ${card.suggested_by}` : 'Suggested work'}
      </p>
      <p className="mt-0.5 text-sm font-semibold text-slate-900"><Markdown inline>{card.initiative_title || 'New work'}</Markdown></p>
      {card.why ? <Markdown className="mt-1 text-sm text-slate-700">{card.why}</Markdown> : null}
      {card.goal ? <p className="mt-1 text-xs text-slate-500">Goal: <Markdown inline>{card.goal}</Markdown></p> : null}
      {steps.length ? (
        <ol className="mt-2 list-decimal space-y-0.5 pl-5 text-xs text-slate-600">
          {steps.map((step) => <li key={step}><Markdown inline>{step}</Markdown></li>)}
        </ol>
      ) : null}
      {decided ? (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-600">
          <Check size={13} className={status === 'dismissed' ? 'text-slate-400' : 'text-emerald-600'} />
          {status === 'dismissed' ? 'Not now' : 'Started'}
          {status !== 'dismissed' ? <span className="ml-1"><InitiativeLink card={card} /></span> : null}
        </p>
      ) : readOnly ? (
        <p className="mt-2 text-xs text-slate-500">Not started</p>
      ) : (
        <div className="mt-3 flex gap-2">
          <button type="button" disabled={Boolean(busy)} onClick={() => act('start')} className="inline-flex items-center gap-1 rounded-lg bg-sky-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-600 disabled:opacity-50">
            {busy === 'start' ? <Loader2 size={13} className="animate-spin" /> : null}Start
          </button>
          {suggested ? (
            <button type="button" disabled={Boolean(busy)} onClick={() => act('dismiss')} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              Not now
            </button>
          ) : null}
        </div>
      )}
      {error ? <p role="alert" className="mt-2 text-xs text-rose-700">{error}</p> : null}
    </div>
  );
}

const REVISION_OUTCOMES = {
  live: 'Approved and applied. It is live.',
  replaced: 'Replaced by a newer draft.',
  discarded: 'Discarded. Nothing was applied.',
  gone: 'No longer available.',
};

/**
 * Changes saved for the person's go-ahead, read live from their branch: still a
 * draft, applied, replaced by a newer draft, or discarded. While it is a draft,
 * Approve and apply makes it live, Review changes shows exactly what it changes,
 * and Discard drops it.
 */
function RevisionReviewCard({ card }) {
  const branch = useSandboxChanges({ sandboxId: card.branch_id, overlayId: card.revision_id || null });
  // The review page's own approval lifecycle, so applying here records the same approval.
  const publication = useBranchPublication({
    branch: branch.data?.sandbox,
    review: branch.data?.review,
    context: { branchId: card.branch_id, revisionId: card.revision_id, contentHash: card.content_hash },
    ready: Boolean(branch.data && card.revision_id && card.content_hash),
  });
  const archive = useSandboxArchive();
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const status = branch.data?.sandbox?.sandbox_status;
  const head = branch.data?.review?.head_revision_id;
  const state = branch.isError ? 'gone'
    : !branch.data ? 'loading'
    : status === 'published' ? (head === card.revision_id ? 'live' : 'replaced')
    : ['discarded', 'archived'].includes(status) ? 'discarded'
    : card.revision_id && head && head !== card.revision_id ? 'replaced'
    : 'draft'; // Awaiting approval too: the button's approval lifecycle says so.
  const busy = publication.busy || archive.isPending;
  const failure = publication.error || publicationError(archive.error?.message);
  const revision = card.revision_number != null ? `Revision ${card.revision_number}` : 'Proposed changes';
  return (
    <div className="animate-slideUp rounded-xl border border-violet-200 bg-white p-4 shadow-sm" data-testid="revision-review-card">
      <p className="text-sm font-semibold text-slate-900"><Markdown inline>{card.subject || 'Ready for your approval'}</Markdown></p>
      <p className="mt-0.5 text-xs text-slate-500"><Markdown inline>{card.title ? `${revision} · ${card.title}` : revision}</Markdown></p>
      {card.summary ? <Markdown className="mt-1 text-sm text-slate-700">{card.summary}</Markdown> : null}
      {REVISION_OUTCOMES[state] ? (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-600" data-testid="revision-review-outcome">
          <Check size={13} className={state === 'live' ? 'text-emerald-600' : 'text-slate-400'} />
          {REVISION_OUTCOMES[state]}
        </p>
      ) : state === 'draft' && confirmDiscard ? (
        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
          <span className="text-slate-700">Discard these changes? Nothing is applied.</span>
          <button type="button" disabled={busy} onClick={() => archive.mutate({ sandboxId: card.branch_id })} className="font-semibold text-rose-700 disabled:opacity-40">
            Confirm discard
          </button>
          <button type="button" disabled={busy} onClick={() => setConfirmDiscard(false)} className="text-slate-600">Cancel</button>
        </div>
      ) : state === 'draft' ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {card.revision_id && card.content_hash ? (
            <button
              type="button"
              disabled={publication.disabled}
              onClick={publication.publish}
              className="inline-flex items-center gap-1 rounded-lg bg-slate-950 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {publication.busy ? <Loader2 size={13} className="animate-spin" /> : null}{publication.label}
            </button>
          ) : null}
          {card.revision_id ? (
            <Link
              to={draftReviewPath(card.branch_id, { revisionId: card.revision_id })}
              className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              Review changes
              <ArrowUpRight size={12} />
            </Link>
          ) : null}
          <button type="button" disabled={busy} onClick={() => setConfirmDiscard(true)} className="rounded-lg px-2 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50">
            Discard
          </button>
        </div>
      ) : (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-400"><Loader2 size={13} className="animate-spin" />Checking…</p>
      )}
      {state === 'draft' && card.revision_id && publication.hint && !failure ?<p className="mt-2 text-xs text-slate-500">{publication.hint}</p> : null}
      {failure ? <p role="alert" className="mt-2 text-xs text-rose-700">{failure}</p> : null}
    </div>
  );
}

function UpdateCard({ card }) {
  return (
    <div className="animate-slideUp rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-sm font-semibold text-slate-900"><Markdown inline>{card.subject || 'Update'}</Markdown></p>
      <InitiativeLink card={card} />
    </div>
  );
}

/**
 * Live cards for an agent's questions, approvals and suggested work, wherever
 * they appear (Home, Needs you, a work page, under a reply). Each reads the
 * current state of its question or work, so answering or starting it anywhere
 * shows everywhere.
 */
export default function AgentCard({ card, bare = false, onAnswered }) {
  if (!card || typeof card !== 'object') return null;
  if (card.approval_request_id) {
    return (
      <div className="animate-slideUp rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <p className="mb-1 text-sm font-semibold text-slate-900"><Markdown inline>{card.subject || 'Approval needed'}</Markdown></p>
        <InitiativeLink card={card} />
        <div className="mt-2">
          <ApprovalReview approvalId={card.approval_request_id} compact />
        </div>
      </div>
    );
  }
  if (card.card_type === 'revision_review' && card.branch_id) return <RevisionReviewCard card={card} />;
  if (card.card_type === 'initiative_ask') return <AskCard card={card} bare={bare} onAnswered={onAnswered} />;
  if (card.card_type === 'reply_passed_on') return <ReplyPassedOnCard card={card} />;
  if (card.card_type === 'work_proposal') return <ProposalCard card={card} bare={bare} />;
  // Chats saved before reports moved to Home can hold a run report's link to its work.
  if (card.card_type === 'work_ref') return <div className="px-0.5"><InitiativeLink card={card} /></div>;
  return <UpdateCard card={card} />;
}
